import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, RadioGroup, ResultTable } from '../_fitnessHelpers';

export default function GFRCalc() {
  const [tab, setTab] = useState(0);
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState(45);
  const [creatMg, setCreatMg] = useState(1.0);
  const [creatUmol, setCreatUmol] = useState(88);
  const [wtLb, setWtLb] = useState(180);
  const [wtKg, setWtKg] = useState(82);
  const clear = () => { setGender('male'); setAge(45); setCreatMg(1.0); setCreatUmol(88); setWtLb(180); setWtKg(82); };

  const c = useMemo(() => {
    const scr = tab === 0 ? +creatMg||0 : (+creatUmol||0)/88.4;
    const kappa = gender === 'female' ? 0.7 : 0.9;
    const alpha = gender === 'female' ? -0.241 : -0.302;
    const female = gender === 'female' ? 1.012 : 1.0;
    const A = Math.min(scr/kappa, 1), B = Math.max(scr/kappa, 1);
    const ckd = 142 * Math.pow(A, alpha) * Math.pow(B, -1.200) * Math.pow(0.9938, age) * female;
    const mdrd = 175 * Math.pow(scr, -1.154) * Math.pow(age, -0.203) * (gender==='female'?0.742:1);
    const wKg = tab === 0 ? (+wtLb||0)*0.453592 : +wtKg||0;
    const cg = ((140 - age) * wKg / (72 * scr)) * (gender==='female'?0.85:1);
    let stage;
    if (ckd >= 90) stage = 'Stage 1 — Normal';
    else if (ckd >= 60) stage = 'Stage 2 — Mildly decreased';
    else if (ckd >= 45) stage = 'Stage 3a — Mild-to-moderate';
    else if (ckd >= 30) stage = 'Stage 3b — Moderate-to-severe';
    else if (ckd >= 15) stage = 'Stage 4 — Severely decreased';
    else stage = 'Stage 5 — Kidney failure';
    return { ckd, mdrd, cg, stage };
  }, [tab, gender, age, creatMg, creatUmol, wtLb, wtKg]);

  return (
    <CalcShell look="cnet" title="GFR Calculator" subtitle="Estimate glomerular filtration rate (eGFR) using CKD-EPI 2021, MDRD, and Cockcroft-Gault.">
      <CalcNetPanel slug="gfr" activeTab={tab} onTabChange={setTab} onClear={clear} right={
        <div>
          <div className="text-lg text-slate-800">eGFR (CKD-EPI 2021)</div>
          <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="gfr-result">{c.ckd.toFixed(1)} mL/min/1.73m²</div>
          <div className="p-3 rounded text-sm mt-3 bg-slate-50">CKD Stage: <b>{c.stage}</b></div>
          <ResultTable testid="gfr-detail-table" rows={[
            ['MDRD estimate', `${c.mdrd.toFixed(1)} mL/min/1.73m²`],
            ['Cockcroft-Gault (creatinine clearance)', `${c.cg.toFixed(1)} mL/min`],
          ]}/>
        </div>
      }>
        <Row label="Gender"><RadioGroup name="gfr-g" value={gender} onChange={setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]}/></Row>
        <Row label="Age"><UnitInput value={age} onChange={setAge} testid="gfr-age"/></Row>
        {tab === 0 ? <>
          <Row label="Serum creatinine"><UnitInput value={creatMg} onChange={setCreatMg} unit="mg/dL" testid="gfr-creat"/></Row>
          <Row label="Weight (for CG)"><UnitInput value={wtLb} onChange={setWtLb} unit="lb" testid="gfr-w"/></Row>
        </> : <>
          <Row label="Serum creatinine"><UnitInput value={creatUmol} onChange={setCreatUmol} unit="μmol/L" testid="gfr-creat-umol"/></Row>
          <Row label="Weight (for CG)"><UnitInput value={wtKg} onChange={setWtKg} unit="kg" testid="gfr-wkg"/></Row>
        </>}
      </CalcNetPanel>
    </CalcShell>
  );
}
